import { useAsyncSelectOptions } from "../../../_hooks/useAsyncSelectOptions";
import { useGetAllRolesQuery } from "../../../_features/roles/api/roleApiSlice";
import { Select } from "../../ui";

/**
 * Async searchable Role picker, built on the shared `useAsyncSelectOptions`
 * hook (see its own docstring for the debounce/pagination/de-dup contract).
 *
 * Roles is the one referenceType that actually needed this. Every other
 * "pick a market/product/..." field is backed by a dedicated, genuinely
 * unbounded "get everything for this org" backend endpoint (confirmed by
 * reading each controller directly), so those are unaffected by this pass.
 * Roles has no such endpoint - the only backend route is the paginated
 * `/role/` list, which defaults to a 10-row page. That's the truncation bug
 * this replaces, at both of its call sites:
 * - `useResolveFormSchema`'s `useGetAllRolesQuery({})` (no limit at all,
 *   truncates at the backend's default of 10).
 * - `useUserManagementFormSchema`'s `useGetAllRolesQuery({ limit: 1000 })`
 *   band-aid (raises the ceiling instead of removing it).
 *
 * `value`/`onChange` carry the whole role object, matching every other
 * entity-object field in this form engine (see renderInput.jsx's "company"/
 * "market" cases) - `userManagementFormRules.js` unwraps `role._id` at
 * submit time.
 */
const RoleAsyncSelect = ({
  label,
  value,
  onChange,
  isMulti = false,
  disabled = false,
  error,
  onBlur,
  excludeId,
}) => {
  const { options, isLoading, hasMore, onSearch, onLoadMore } = useAsyncSelectOptions({
    useListQuery: useGetAllRolesQuery,
    buildParams: (page, limit, search) => ({
      page,
      limit,
      filters: JSON.stringify(search ? { sort: "name", search } : { sort: "name" }),
    }),
    responseKey: "roles",
  });

  // `excludeId` - e.g. the role currently being deleted, when this is used
  // as a "reassign affected users to..." picker that must not offer the
  // very role it's replacing.
  const roles = options.filter(
    (role) => role?.name?.trim()?.length > 0 && role?._id !== excludeId
  );
  const roleOptions = roles.map((role) => ({ label: role.name, value: role._id }));

  if (isMulti) {
    const selectedValues = (value || []).map((r) => r?._id);
    return (
      <Select
        label={label}
        options={roleOptions}
        isMulti
        value={selectedValues}
        onChange={(ids) => onChange(roles.filter((r) => ids.includes(r._id)))}
        onSearch={onSearch}
        isLoading={isLoading}
        hasMore={hasMore}
        onLoadMore={onLoadMore}
        disabled={disabled}
        error={error}
        onBlur={onBlur}
      />
    );
  }

  return (
    <Select
      label={label}
      options={roleOptions}
      value={value?._id}
      selectedLabel={value?.name}
      onChange={(id) => {
        const picked = roles.find((r) => r._id === id);
        if (picked) onChange(picked);
      }}
      onSearch={onSearch}
      isLoading={isLoading}
      hasMore={hasMore}
      onLoadMore={onLoadMore}
      disabled={disabled}
      error={error}
      onBlur={onBlur}
    />
  );
};

export default RoleAsyncSelect;
